import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Link, Navigate } from '@tanstack/react-router'
import { FolderOpen, Pencil, Plus, Shield, Trash2, User as UserIcon } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { PersonnelLayout } from '../components/personnel-layout'
import { MobileBottomSlots } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Dialog } from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus } from '../components/ui/mobile-action-bar'
import { Select } from '../components/ui/select'
import {
  api,
  type HourAdjustment,
  type Role,
  type User,
  type VacationAdjustment,
  type VacationEntitlement,
  type WorkSchedule,
} from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/users',
  component: UsersPage,
})

function UsersPage() {
  const [dialog, setDialog] = useState<{ open: boolean; user: User | null }>({
    open: false,
    user: null,
  })
  const [showArchived, setShowArchived] = useState(false)

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data, isLoading } = useQuery({
    queryKey: ['users', { archived: showArchived }],
    queryFn: () => api.listUsers(showArchived),
    enabled: meData?.user?.role === 'admin',
  })
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.listRoles(),
    enabled: meData?.user?.role === 'admin',
  })
  const allRoles = rolesData?.roles ?? []

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (meData.user.role !== 'admin') {
    return (
      <PersonnelLayout title="Mitarbeiter">
        <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-8 text-center">
          <p className="text-text-secondary">
            Mitarbeiter-Verwaltung ist nur für Admin-User sichtbar.
          </p>
        </div>
      </PersonnelLayout>
    )
  }

  const users = data?.users ?? []

  return (
    <PersonnelLayout
      title="Mitarbeiter"
      description="Konten anlegen, Rollen + Stundensätze setzen. Passwörter werden gehasht (Argon2id), Sessions werden bei Passwort-Reset invalidiert."
    >
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): ＋ Neuer Mitarbeiter in der Bottom-Bar —
          derselbe Handler wie der Kopf-Button. */}
      <MobileBottomSlots>
        <BarAccentPlus
          ariaLabel="Neuer Mitarbeiter"
          onClick={() => setDialog({ open: true, user: null })}
        />
      </MobileBottomSlots>

      {/* <640 übernimmt die Bottom-Bar — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <div className="flex phone:hidden justify-end mb-4">
        <Button onClick={() => setDialog({ open: true, user: null })}>
          <Plus className="h-4 w-4" /> Neuer Mitarbeiter
        </Button>
      </div>

      <button
        type="button"
        onClick={() => setShowArchived((v) => !v)}
        className="text-xs text-text-tertiary hover:text-accent transition-colors"
      >
        {showArchived ? 'Aktive zeigen' : 'Deaktivierte zeigen'}
      </button>

      {isLoading ? (
        <p className="text-text-secondary">Lade …</p>
      ) : users.length === 0 ? (
        <p className="text-text-secondary">Keine Mitarbeiter.</p>
      ) : (
        <ul className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated/40">
          {users.map((u, i) => (
            <UserRow
              key={u.id}
              user={u}
              isLast={i === users.length - 1}
              isSelf={u.id === meData.user.id}
              onEdit={() => setDialog({ open: true, user: u })}
              roles={allRoles}
            />
          ))}
        </ul>
      )}

      <UserDialog
        open={dialog.open}
        user={dialog.user}
        onClose={() => setDialog({ open: false, user: null })}
        roles={allRoles}
        allUsers={users}
      />
    </PersonnelLayout>
  )
}

function UserRow({
  user,
  isLast,
  isSelf,
  onEdit,
  roles,
}: {
  user: User
  isLast: boolean
  isSelf: boolean
  onEdit: () => void
  roles: Role[]
}) {
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: () => api.deleteUser(user.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      notify.success('Mitarbeiter deaktiviert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const rate = user.hourlyRateCents != null ? `${(user.hourlyRateCents / 100).toFixed(2)} €/h` : '—'
  const assignedRole = user.roleId ? roles.find((r) => r.id === user.roleId) : null
  const roleName = assignedRole?.name ?? user.role
  const isAdminRole = user.role === 'admin' || (assignedRole?.isAdmin ?? false)

  return (
    <li
      className={`group grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-4 px-5 py-4 transition-colors hover:bg-bg-overlay/40 ${
        !isLast ? 'border-b border-border-subtle' : ''
      } ${user.archived ? 'opacity-60' : ''}`}
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/15 text-accent text-xs font-semibold">
        {user.name.slice(0, 2).toUpperCase()}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-text-primary truncate">
          {user.name} {isSelf && <span className="text-xs text-text-tertiary">· du</span>}
        </p>
        <p className="text-xs text-text-tertiary truncate">{user.email}</p>
      </div>
      <p className="font-mono text-sm text-accent tabular-nums">{rate}</p>
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] border ${
          isAdminRole
            ? 'bg-accent/15 border-accent/40 text-accent'
            : 'bg-info/15 border-info/40 text-info'
        }`}
      >
        {isAdminRole ? <Shield className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}
        {roleName}
      </span>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
        <Link
          to="/users/$id/dossier"
          params={{ id: user.id }}
          aria-label="Akte öffnen"
          title="Mitarbeiterakte öffnen"
          className="text-text-tertiary hover:text-accent transition-colors p-2 rounded-md hover:bg-bg-overlay"
        >
          <FolderOpen className="h-4 w-4" />
        </Link>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onEdit}
          aria-label="Bearbeiten"
          className="text-text-tertiary hover:text-accent"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        {!isSelf && !user.archived && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={async () => {
              const ok = await confirm({
                title: 'Mitarbeiter deaktivieren',
                description: `${user.name} wird deaktiviert; bestehende Daten bleiben erhalten.`,
                variant: 'danger',
                confirmLabel: 'Deaktivieren',
              })
              if (ok) remove.mutate()
            }}
            aria-label="Deaktivieren"
            className="text-text-tertiary hover:text-danger"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </li>
  )
}

function UserDialog({
  open,
  user,
  onClose,
  roles: availableRoles,
  allUsers,
}: {
  open: boolean
  user: User | null
  onClose: () => void
  roles: Role[]
  allUsers: User[]
}) {
  const queryClient = useQueryClient()
  const isEdit = !!user
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<'admin' | 'user'>('user')
  const [roleId, setRoleId] = useState<string>('')
  const [rate, setRate] = useState('')
  const [lockMode, setLockMode] = useState<'none' | 'dynamic' | 'static' | 'full'>('none')
  const [lockDynDays, setLockDynDays] = useState('7')
  const [lockUntil, setLockUntil] = useState('')
  const [supervisorId, setSupervisorId] = useState<string>('')
  const [weeklyHours, setWeeklyHours] = useState('')
  const [vacationDays, setVacationDays] = useState('')
  const [personnelNumber, setPersonnelNumber] = useState('')
  const [tab, setTab] = useState<'general' | 'lock' | 'arbeitszeit'>('general')

  useEffect(() => {
    if (!open) return
    if (user) {
      setName(user.name)
      setEmail(user.email)
      setRole(user.role)
      setRoleId(user.roleId ?? '')
      setRate(user.hourlyRateCents != null ? String(user.hourlyRateCents / 100) : '')
      setPassword('')
      setLockMode(user.editLockMode ?? 'none')
      setLockDynDays(String(user.editLockDynDays ?? 7))
      setLockUntil(user.editLockUntil ? user.editLockUntil.slice(0, 10) : '')
      setSupervisorId(user.supervisorId ?? '')
      // Beim Edit gibt's keine Initial-Werte mehr — Arbeitszeit + Urlaub
      // werden im Arbeitszeit-Tab konfiguriert.
      setWeeklyHours('')
      setVacationDays('')
      setPersonnelNumber(user.personnelNumber ?? '')
    } else {
      setName('')
      setEmail('')
      setPassword('')
      setRole('user')
      setRoleId('')
      setRate('')
      setLockMode('none')
      setLockDynDays('7')
      setLockUntil('')
      setSupervisorId('')
      setWeeklyHours('')
      setVacationDays('')
      setPersonnelNumber('')
    }
    setTab('general')
  }, [open, user])

  const save = useMutation({
    mutationFn: async () => {
      const cents = rate ? Math.round(Number(rate) * 100) : null
      // Sync legacy role field from custom role's isAdmin flag
      const selectedRole = roleId ? availableRoles.find((r) => r.id === roleId) : null
      const legacyRole = selectedRole?.isAdmin ? 'admin' : role
      const trimmedPersonnelNumber = personnelNumber.trim() || null
      if (isEdit && user) {
        const data: Parameters<typeof api.updateUser>[1] = {
          name,
          email,
          role: legacyRole,
          roleId: roleId || null,
          hourlyRateCents: cents,
          editLockMode: lockMode,
          editLockDynDays: lockMode === 'dynamic' ? Number(lockDynDays) : null,
          editLockUntil: lockMode === 'static' && lockUntil ? lockUntil : null,
          supervisorId: supervisorId || null,
          personnelNumber: trimmedPersonnelNumber,
        }
        if (password) data.password = password
        return api.updateUser(user.id, data)
      }
      // Create: User selbst anlegen + Initial-Arbeitszeitmodell + Initial-
      // Urlaubsanspruch aus den Wochenstunden/Urlaubstagen direkt
      // erzeugen, sodass Saldo und Kalender ab Tag 1 stimmen.
      const created = await api.createUser({
        name,
        email,
        password,
        role: legacyRole,
        roleId: roleId || null,
        hourlyRateCents: cents,
        personnelNumber: trimmedPersonnelNumber,
      })
      const newUserId = created.user.id
      const weekly = Number(weeklyHours)
      if (weekly > 0) {
        const perDay = Math.round((weekly / 5) * 100) / 100
        await api.createWorkSchedule({
          userId: newUserId,
          label: 'Standard',
          validFrom: new Date().toISOString().slice(0, 10),
          validTo: null,
          weeklyHours: weekly,
          monHours: perDay,
          tueHours: perDay,
          wedHours: perDay,
          thuHours: perDay,
          friHours: perDay,
          satHours: 0,
          sunHours: 0,
        })
      }
      const vac = Number(vacationDays)
      if (vac > 0) {
        await api.createVacationEntitlement({
          userId: newUserId,
          validFrom: `${new Date().getFullYear()}-01-01`,
          validTo: null,
          daysPerYear: vac,
        })
      }
      return created
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      notify.success(isEdit ? 'Mitarbeiter aktualisiert' : 'Mitarbeiter angelegt')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !email.trim()) {
      notify.error('Name und E-Mail sind Pflicht')
      return
    }
    if (!isEdit && !password) {
      notify.error('Passwort ist Pflicht beim Anlegen')
      return
    }
    if (password && password.length < 8) {
      notify.error('Passwort muss mind. 8 Zeichen haben')
      return
    }
    save.mutate()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? `${user?.name} bearbeiten` : 'Neuer Mitarbeiter'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} type="button" disabled={save.isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4 pb-2">
        {/* Tab bar (only for edit) */}
        {isEdit && (
          <div className="flex gap-1 border-b border-border-subtle -mx-1 mb-1">
            {(['general', 'arbeitszeit', 'lock'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={cn(
                  'px-3 py-1.5 text-xs border-b-2 transition-colors',
                  tab === t
                    ? 'border-accent text-accent'
                    : 'border-transparent text-text-secondary hover:text-text-primary',
                )}
              >
                {t === 'general'
                  ? 'Allgemein'
                  : t === 'arbeitszeit'
                    ? 'Arbeitszeit'
                    : t === 'lock'
                      ? 'Sperre'
                      : 'Sichtbarkeit'}
              </button>
            ))}
          </div>
        )}

        {(!isEdit || tab === 'general') && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="usr-name">Name</Label>
                <Input
                  id="usr-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="usr-email">E-Mail</Label>
                <Input
                  id="usr-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="usr-role">Rolle</Label>
                {availableRoles.length > 0 ? (
                  <Select
                    id="usr-role"
                    value={roleId}
                    onChange={(e) => {
                      setRoleId(e.target.value)
                      // Sync legacy role field
                      const r = availableRoles.find((r) => r.id === e.target.value)
                      if (r?.isAdmin) setRole('admin')
                      else setRole('user')
                    }}
                  >
                    <option value="">— Keine Rolle —</option>
                    {availableRoles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                        {r.isAdmin ? ' (Admin)' : ''}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Select
                    id="usr-role"
                    value={role}
                    onChange={(e) => setRole(e.target.value as 'admin' | 'user')}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </Select>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="usr-rate">Stundensatz (€)</Label>
                <Input
                  id="usr-rate"
                  type="number"
                  min="0"
                  step="0.5"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  placeholder="optional"
                />
              </div>
            </div>
            {isEdit && (
              <div className="space-y-2">
                <Label htmlFor="usr-supervisor">Vorgesetzter</Label>
                <Select
                  id="usr-supervisor"
                  value={supervisorId}
                  onChange={(e) => setSupervisorId(e.target.value)}
                >
                  <option value="">— Kein Vorgesetzter —</option>
                  {allUsers
                    .filter((u) => !u.archived && u.id !== user?.id)
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                </Select>
                <p className="text-2xs text-text-tertiary">
                  Abwesenheiten dieses Mitarbeiters müssen vom Vorgesetzten genehmigt werden.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="usr-personnel-nr">Personalnummer</Label>
              <Input
                id="usr-personnel-nr"
                type="text"
                value={personnelNumber}
                onChange={(e) => setPersonnelNumber(e.target.value)}
                placeholder="optional, z.B. 0042"
              />
              <p className="text-2xs text-text-tertiary">
                Wird in der Team-Jahresübersicht (Stundenkonten) angezeigt.
              </p>
            </div>
            {!isEdit && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="usr-weekly-hours">Wochenstunden</Label>
                  <Input
                    id="usr-weekly-hours"
                    type="number"
                    min="0"
                    max="168"
                    step="0.5"
                    value={weeklyHours}
                    onChange={(e) => setWeeklyHours(e.target.value)}
                    placeholder="z.B. 40"
                  />
                  <p className="text-2xs text-text-tertiary">
                    Erzeugt initial ein Arbeitszeitmodell (gleichmäßig Mo–Fr). Spätere Änderungen im
                    Reiter Arbeitszeit.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="usr-vacation-days">Urlaubstage/Jahr</Label>
                  <Input
                    id="usr-vacation-days"
                    type="number"
                    min="0"
                    max="365"
                    step="0.5"
                    value={vacationDays}
                    onChange={(e) => setVacationDays(e.target.value)}
                    placeholder="z.B. 30"
                  />
                  <p className="text-2xs text-text-tertiary">
                    Erzeugt initial einen Urlaubsanspruch ab 1.1. dieses Jahres. Spätere Änderungen
                    im Reiter Arbeitszeit.
                  </p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="usr-password">
                {isEdit ? 'Neues Passwort (leer = unverändert)' : 'Passwort'}
              </Label>
              <Input
                id="usr-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isEdit ? '••••••••' : 'min. 8 Zeichen'}
                autoComplete="new-password"
              />
              {isEdit && password && (
                <p className="text-xs text-warning">
                  Achtung: bei Passwort-Reset werden alle aktiven Sessions des Users beendet.
                </p>
              )}
            </div>
          </div>
        )}

        {isEdit && tab === 'arbeitszeit' && user && <ArbeitszeitTab userId={user.id} />}

        {isEdit && tab === 'lock' && (
          <div className="space-y-3">
            <p className="text-xs text-text-tertiary">
              Verhindert nachträgliche Änderungen an Zeiteinträgen. User muss Änderungsantrag
              stellen.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Select
                value={lockMode}
                onChange={(e) => setLockMode(e.target.value as typeof lockMode)}
              >
                <option value="none">Keine Sperre</option>
                <option value="dynamic">Dynamisch (rollierend)</option>
                <option value="static">Statisch (festes Datum)</option>
                <option value="full">Vollständig (alles vor heute)</option>
              </Select>
              {lockMode === 'dynamic' && (
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={lockDynDays}
                  onChange={(e) => setLockDynDays(e.target.value)}
                  placeholder="Tage rückwärts"
                />
              )}
              {lockMode === 'static' && (
                <Input
                  type="date"
                  value={lockUntil}
                  onChange={(e) => setLockUntil(e.target.value)}
                />
              )}
            </div>
          </div>
        )}
      </form>
    </Dialog>
  )
}

// ─── Arbeitszeit Tab ─────────────────────────────────────────────────

const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const
const ADJ_TYPE_LABELS: Record<string, string> = {
  carryover: 'Vortrag',
  payout: 'Auszahlung',
  correction: 'Korrektur',
}
const VAC_ADJ_TYPE_LABELS: Record<string, string> = {
  carryover: 'Übertrag',
  payout: 'Auszahlung',
  expiry: 'Verfall',
  correction: 'Korrektur',
}

function fmtDiffHHMM(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+'
  const abs = Math.abs(minutes)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function ArbeitszeitTab({ userId }: { userId: string }) {
  const queryClient = useQueryClient()

  // ── Schedules ──────────────────────────────────────────────────────
  const { data: schedData } = useQuery({
    queryKey: ['work-schedules', userId],
    queryFn: () => api.listWorkSchedules(userId),
  })
  const schedules = schedData?.schedules ?? []

  const [showSchedForm, setShowSchedForm] = useState(false)
  const [editingSchedId, setEditingSchedId] = useState<string | null>(null)
  const [schedLabel, setSchedLabel] = useState('')
  const [schedFrom, setSchedFrom] = useState('')
  const [schedTo, setSchedTo] = useState('')
  const [schedWeekly, setSchedWeekly] = useState('40')
  const [schedDays, setSchedDays] = useState(['8', '8', '8', '8', '8', '0', '0'])

  function resetSchedForm() {
    setShowSchedForm(false)
    setEditingSchedId(null)
    setSchedLabel('')
    setSchedFrom('')
    setSchedTo('')
    setSchedWeekly('40')
    setSchedDays(['8', '8', '8', '8', '8', '0', '0'])
  }

  function startEditSched(s: WorkSchedule) {
    setEditingSchedId(s.id)
    setSchedLabel(s.label ?? '')
    setSchedFrom(s.validFrom)
    setSchedTo(s.validTo ?? '')
    setSchedWeekly(Number(s.weeklyHours).toString())
    setSchedDays([
      Number(s.monHours).toString(),
      Number(s.tueHours).toString(),
      Number(s.wedHours).toString(),
      Number(s.thuHours).toString(),
      Number(s.friHours).toString(),
      Number(s.satHours).toString(),
      Number(s.sunHours).toString(),
    ])
    setShowSchedForm(true)
  }

  const saveSched = useMutation({
    mutationFn: () => {
      const payload = {
        label: schedLabel || null,
        validFrom: schedFrom,
        validTo: schedTo || null,
        weeklyHours: Number(schedWeekly),
        monHours: Number(schedDays[0]),
        tueHours: Number(schedDays[1]),
        wedHours: Number(schedDays[2]),
        thuHours: Number(schedDays[3]),
        friHours: Number(schedDays[4]),
        satHours: Number(schedDays[5]),
        sunHours: Number(schedDays[6]),
      }
      if (editingSchedId) {
        return api.updateWorkSchedule(editingSchedId, payload)
      }
      return api.createWorkSchedule({ userId, ...payload, validTo: schedTo || undefined })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-schedules', userId] })
      // Arbeitszeitmodell = Soll-Quelle → Berichte/Stundenkonto mit.
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success(editingSchedId ? 'Zeitraum aktualisiert' : 'Zeitraum angelegt')
      resetSchedForm()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteSched = useMutation({
    mutationFn: (id: string) => api.deleteWorkSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-schedules', userId] })
      // Arbeitszeitmodell = Soll-Quelle → Berichte/Stundenkonto mit.
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success('Zeitraum gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // Auto-fill Mo-Fr from weekly hours
  function onWeeklyChange(val: string) {
    setSchedWeekly(val)
    const weekly = Number(val)
    if (!Number.isNaN(weekly) && weekly >= 0) {
      const perDay = (weekly / 5).toFixed(2)
      setSchedDays([perDay, perDay, perDay, perDay, perDay, '0', '0'])
    }
  }

  // ── Adjustments ────────────────────────────────────────────────────
  const { data: adjData } = useQuery({
    queryKey: ['hour-adjustments', userId],
    queryFn: () => api.listHourAdjustments({ userId }),
  })
  const adjustments = adjData?.adjustments ?? []

  const [showAdjForm, setShowAdjForm] = useState(false)
  const [editingAdjId, setEditingAdjId] = useState<string | null>(null)
  const [adjType, setAdjType] = useState<'carryover' | 'payout' | 'correction'>('carryover')
  const [adjDate, setAdjDate] = useState('')
  const [adjHours, setAdjHours] = useState('')
  const [adjDesc, setAdjDesc] = useState('')

  function resetAdjForm() {
    setShowAdjForm(false)
    setEditingAdjId(null)
    setAdjType('carryover')
    setAdjDate('')
    setAdjHours('')
    setAdjDesc('')
  }

  function startEditAdj(a: HourAdjustment) {
    setEditingAdjId(a.id)
    setAdjType(a.type as 'carryover' | 'payout' | 'correction')
    setAdjDate(a.date)
    setAdjHours((a.minutes / 60).toString())
    setAdjDesc(a.description ?? '')
    setShowAdjForm(true)
  }

  const saveAdj = useMutation({
    mutationFn: () => {
      const payload = {
        type: adjType,
        date: adjDate,
        minutes: Math.round(Number(adjHours) * 60),
        description: adjDesc || null,
      }
      if (editingAdjId) {
        return api.updateHourAdjustment(editingAdjId, payload)
      }
      return api.createHourAdjustment({ userId, ...payload })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hour-adjustments', userId] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success(editingAdjId ? 'Anpassung aktualisiert' : 'Anpassung angelegt')
      resetAdjForm()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteAdj = useMutation({
    mutationFn: (id: string) => api.deleteHourAdjustment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hour-adjustments', userId] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success('Anpassung gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // ── Vacation Entitlements ──────────────────────────────────────────
  const { data: entData } = useQuery({
    queryKey: ['vacation-entitlements', userId],
    queryFn: () => api.listVacationEntitlements(userId),
  })
  const entitlements = entData?.entitlements ?? []

  const [showEntForm, setShowEntForm] = useState(false)
  const [editingEntId, setEditingEntId] = useState<string | null>(null)
  const [entFrom, setEntFrom] = useState('')
  const [entTo, setEntTo] = useState('')
  const [entDays, setEntDays] = useState('30')
  const [entNotes, setEntNotes] = useState('')

  function resetEntForm() {
    setShowEntForm(false)
    setEditingEntId(null)
    setEntFrom('')
    setEntTo('')
    setEntDays('30')
    setEntNotes('')
  }

  function startEditEnt(e: VacationEntitlement) {
    setEditingEntId(e.id)
    setEntFrom(e.validFrom)
    setEntTo(e.validTo ?? '')
    setEntDays(Number(e.daysPerYear).toString())
    setEntNotes(e.notes ?? '')
    setShowEntForm(true)
  }

  const saveEnt = useMutation({
    mutationFn: () => {
      const payload = {
        validFrom: entFrom,
        validTo: entTo || null,
        daysPerYear: Number(entDays),
        notes: entNotes || null,
      }
      if (editingEntId) {
        return api.updateVacationEntitlement(editingEntId, payload)
      }
      return api.createVacationEntitlement({ userId, ...payload })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-entitlements', userId] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success(editingEntId ? 'Urlaubsanspruch aktualisiert' : 'Urlaubsanspruch angelegt')
      resetEntForm()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteEnt = useMutation({
    mutationFn: (id: string) => api.deleteVacationEntitlement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-entitlements', userId] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success('Urlaubsanspruch gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // ── Vacation Adjustments ───────────────────────────────────────────
  const { data: vacAdjData } = useQuery({
    queryKey: ['vacation-adjustments', userId],
    queryFn: () => api.listVacationAdjustments({ userId }),
  })
  const vacAdjustments = vacAdjData?.adjustments ?? []

  const [showVacAdjForm, setShowVacAdjForm] = useState(false)
  const [editingVacAdjId, setEditingVacAdjId] = useState<string | null>(null)
  const [vacAdjType, setVacAdjType] = useState<'carryover' | 'payout' | 'expiry' | 'correction'>(
    'carryover',
  )
  const [vacAdjYear, setVacAdjYear] = useState(String(new Date().getFullYear()))
  const [vacAdjDays, setVacAdjDays] = useState('')
  const [vacAdjExpires, setVacAdjExpires] = useState('')
  const [vacAdjDesc, setVacAdjDesc] = useState('')

  // Auto-fill expiry when carryover + year change — only in create mode,
  // sonst überschreiben wir den vom Server geladenen Wert beim Edit.
  useEffect(() => {
    if (editingVacAdjId) return
    if (vacAdjType === 'carryover' && vacAdjYear) {
      const y = Number(vacAdjYear)
      if (!Number.isNaN(y)) {
        setVacAdjExpires(`${y + 1}-03-31`)
      }
    } else if (vacAdjType !== 'carryover') {
      setVacAdjExpires('')
    }
  }, [vacAdjType, vacAdjYear, editingVacAdjId])

  function resetVacAdjForm() {
    setShowVacAdjForm(false)
    setEditingVacAdjId(null)
    setVacAdjType('carryover')
    setVacAdjYear(String(new Date().getFullYear()))
    setVacAdjDays('')
    setVacAdjExpires('')
    setVacAdjDesc('')
  }

  function startEditVacAdj(v: VacationAdjustment) {
    setEditingVacAdjId(v.id)
    setVacAdjType(v.type)
    setVacAdjYear(String(v.year))
    setVacAdjDays(Number(v.days).toString())
    setVacAdjExpires(v.expiresAt ?? '')
    setVacAdjDesc(v.description ?? '')
    setShowVacAdjForm(true)
  }

  const saveVacAdj = useMutation({
    mutationFn: () => {
      const payload = {
        type: vacAdjType,
        year: Number(vacAdjYear),
        days: Number(vacAdjDays),
        description: vacAdjDesc || null,
        expiresAt: vacAdjType === 'carryover' ? vacAdjExpires || null : null,
      }
      if (editingVacAdjId) {
        return api.updateVacationAdjustment(editingVacAdjId, payload)
      }
      return api.createVacationAdjustment({ userId, ...payload })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-adjustments', userId] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success(
        editingVacAdjId ? 'Urlaubsanpassung aktualisiert' : 'Urlaubsanpassung angelegt',
      )
      resetVacAdjForm()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteVacAdj = useMutation({
    mutationFn: (id: string) => api.deleteVacationAdjustment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-adjustments', userId] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success('Urlaubsanpassung gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <div className="space-y-6">
      {/* ─── Work Schedules ──────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
            Arbeitszeitmodelle
          </h3>
          <button
            type="button"
            onClick={() => (showSchedForm ? resetSchedForm() : setShowSchedForm(true))}
            className="text-xs text-accent hover:underline"
          >
            {showSchedForm ? 'Abbrechen' : '+ Zeitraum hinzufügen'}
          </button>
        </div>

        {showSchedForm && (
          <div className="rounded-xl border border-border-subtle p-3 mb-3 space-y-3 bg-bg-overlay/30">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-2xs">Bezeichnung</Label>
                <Input
                  value={schedLabel}
                  onChange={(e) => setSchedLabel(e.target.value)}
                  placeholder="z.B. Vollzeit"
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-2xs">Wochenstunden</Label>
                <Input
                  type="number"
                  min="0"
                  max="168"
                  step="0.5"
                  value={schedWeekly}
                  onChange={(e) => onWeeklyChange(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-2xs">Gültig ab</Label>
                <Input
                  type="date"
                  value={schedFrom}
                  onChange={(e) => setSchedFrom(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-2xs">Gültig bis (leer = offen)</Label>
                <Input
                  type="date"
                  value={schedTo}
                  onChange={(e) => setSchedTo(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {WEEKDAY_LABELS.map((label, i) => (
                <div key={label} className="space-y-0.5 text-center">
                  <span className="text-2xs text-text-tertiary">{label}</span>
                  <Input
                    type="number"
                    min="0"
                    max="24"
                    step="0.5"
                    value={schedDays[i]}
                    onChange={(e) => {
                      const next = [...schedDays]
                      next[i] = e.target.value
                      setSchedDays(next)
                    }}
                    className="h-7 text-xs text-center px-1"
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                type="button"
                onClick={() => {
                  if (!schedFrom) {
                    notify.error('Gültig-ab-Datum ist Pflicht')
                    return
                  }
                  saveSched.mutate()
                }}
                disabled={saveSched.isPending}
              >
                Speichern
              </Button>
            </div>
          </div>
        )}

        {schedules.length === 0 ? (
          <p className="text-xs text-text-tertiary">Keine Arbeitszeitmodelle konfiguriert.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border-subtle text-text-tertiary">
                  <th className="text-left py-1 px-1 font-medium">Zeitraum</th>
                  <th className="text-right py-1 px-1 font-medium">Wo/h</th>
                  {WEEKDAY_LABELS.map((d) => (
                    <th key={d} className="text-right py-1 px-0.5 font-medium">
                      {d}
                    </th>
                  ))}
                  <th className="py-1 px-1" />
                </tr>
              </thead>
              <tbody>
                {schedules.map((s) => (
                  <tr key={s.id} className="border-b border-border-subtle hover:bg-bg-overlay/30">
                    <td className="py-1.5 px-1 text-text-primary">
                      {s.validFrom}
                      {s.validTo ? ` – ${s.validTo}` : ' – offen'}
                      {s.label && <span className="ml-1 text-text-tertiary">({s.label})</span>}
                    </td>
                    <td className="py-1.5 px-1 text-right tabular-nums font-medium">
                      {Number(s.weeklyHours).toFixed(2).replace('.', ',')}
                    </td>
                    <td className="py-1.5 px-0.5 text-right tabular-nums">
                      {Number(s.monHours).toFixed(2).replace('.', ',')}
                    </td>
                    <td className="py-1.5 px-0.5 text-right tabular-nums">
                      {Number(s.tueHours).toFixed(2).replace('.', ',')}
                    </td>
                    <td className="py-1.5 px-0.5 text-right tabular-nums">
                      {Number(s.wedHours).toFixed(2).replace('.', ',')}
                    </td>
                    <td className="py-1.5 px-0.5 text-right tabular-nums">
                      {Number(s.thuHours).toFixed(2).replace('.', ',')}
                    </td>
                    <td className="py-1.5 px-0.5 text-right tabular-nums">
                      {Number(s.friHours).toFixed(2).replace('.', ',')}
                    </td>
                    <td className="py-1.5 px-0.5 text-right tabular-nums">
                      {Number(s.satHours).toFixed(2).replace('.', ',')}
                    </td>
                    <td className="py-1.5 px-0.5 text-right tabular-nums">
                      {Number(s.sunHours).toFixed(2).replace('.', ',')}
                    </td>
                    <td className="py-1.5 px-1">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => startEditSched(s)}
                          aria-label="Bearbeiten"
                          className="text-text-tertiary hover:text-accent"
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={async () => {
                            const ok = await confirm({
                              title: 'Zeitraum löschen',
                              description: 'Dieses Arbeitszeitmodell wird unwiderruflich gelöscht.',
                              variant: 'danger',
                              confirmLabel: 'Löschen',
                            })
                            if (ok) deleteSched.mutate(s.id)
                          }}
                          aria-label="Löschen"
                          className="text-text-tertiary hover:text-danger"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Hour Adjustments ────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
            Stundenanpassungen
          </h3>
          <button
            type="button"
            onClick={() => (showAdjForm ? resetAdjForm() : setShowAdjForm(true))}
            className="text-xs text-accent hover:underline"
          >
            {showAdjForm ? 'Abbrechen' : '+ Anpassung hinzufügen'}
          </button>
        </div>

        {showAdjForm && (
          <div className="rounded-xl border border-border-subtle p-3 mb-3 space-y-3 bg-bg-overlay/30">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-2xs">Typ</Label>
                <Select
                  value={adjType}
                  onChange={(e) =>
                    setAdjType(e.target.value as 'carryover' | 'payout' | 'correction')
                  }
                  className="h-8 text-xs"
                >
                  <option value="carryover">Vortrag</option>
                  <option value="payout">Auszahlung</option>
                  <option value="correction">Korrektur</option>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-2xs">Datum</Label>
                <Input
                  type="date"
                  value={adjDate}
                  onChange={(e) => setAdjDate(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-2xs">Stunden (+/-)</Label>
                <Input
                  type="number"
                  step="0.25"
                  value={adjHours}
                  onChange={(e) => setAdjHours(e.target.value)}
                  placeholder="z.B. 10 oder -5"
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-2xs">Beschreibung</Label>
                <Input
                  value={adjDesc}
                  onChange={(e) => setAdjDesc(e.target.value)}
                  placeholder="z.B. Vortrag 2025"
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                type="button"
                onClick={() => {
                  if (!adjDate || !adjHours) {
                    notify.error('Datum und Stunden sind Pflicht')
                    return
                  }
                  saveAdj.mutate()
                }}
                disabled={saveAdj.isPending}
              >
                Speichern
              </Button>
            </div>
          </div>
        )}

        {adjustments.length === 0 ? (
          <p className="text-xs text-text-tertiary">Keine Stundenanpassungen vorhanden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border-subtle text-text-tertiary">
                  <th className="text-left py-1 px-1 font-medium">Datum</th>
                  <th className="text-left py-1 px-1 font-medium">Typ</th>
                  <th className="text-right py-1 px-1 font-medium">Stunden</th>
                  <th className="text-left py-1 px-1 font-medium">Beschreibung</th>
                  <th className="py-1 px-1" />
                </tr>
              </thead>
              <tbody>
                {adjustments.map((a) => (
                  <tr key={a.id} className="border-b border-border-subtle hover:bg-bg-overlay/30">
                    <td className="py-1.5 px-1 text-text-primary tabular-nums">{a.date}</td>
                    <td className="py-1.5 px-1 text-text-secondary">
                      {ADJ_TYPE_LABELS[a.type] ?? a.type}
                    </td>
                    <td
                      className={cn(
                        'py-1.5 px-1 text-right tabular-nums font-medium',
                        a.minutes > 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : a.minutes < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-text-secondary',
                      )}
                    >
                      {fmtDiffHHMM(a.minutes)}
                    </td>
                    <td className="py-1.5 px-1 text-text-tertiary truncate max-w-[140px]">
                      {a.description ?? '—'}
                    </td>
                    <td className="py-1.5 px-1">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => startEditAdj(a)}
                          aria-label="Bearbeiten"
                          className="text-text-tertiary hover:text-accent"
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={async () => {
                            const ok = await confirm({
                              title: 'Anpassung löschen',
                              description: 'Diese Stundenanpassung wird unwiderruflich gelöscht.',
                              variant: 'danger',
                              confirmLabel: 'Löschen',
                            })
                            if (ok) deleteAdj.mutate(a.id)
                          }}
                          aria-label="Löschen"
                          className="text-text-tertiary hover:text-danger"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Vacation Entitlements ───────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
            Urlaubsansprüche
          </h3>
          <button
            type="button"
            onClick={() => (showEntForm ? resetEntForm() : setShowEntForm(true))}
            className="text-xs text-accent hover:underline"
          >
            {showEntForm ? 'Abbrechen' : '+ Zeitraum hinzufügen'}
          </button>
        </div>

        {showEntForm && (
          <div className="rounded-xl border border-border-subtle p-3 mb-3 space-y-3 bg-bg-overlay/30">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-2xs">Gültig ab</Label>
                <Input
                  type="date"
                  value={entFrom}
                  onChange={(e) => setEntFrom(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-2xs">Gültig bis (leer = offen)</Label>
                <Input
                  type="date"
                  value={entTo}
                  onChange={(e) => setEntTo(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-2xs">Tage/Jahr</Label>
                <Input
                  type="number"
                  min="0"
                  max="365"
                  step="0.5"
                  value={entDays}
                  onChange={(e) => setEntDays(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-2xs">Notiz</Label>
                <Input
                  value={entNotes}
                  onChange={(e) => setEntNotes(e.target.value)}
                  placeholder="optional"
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                type="button"
                onClick={() => {
                  if (!entFrom || !entDays) {
                    notify.error('Gültig-ab und Tage/Jahr sind Pflicht')
                    return
                  }
                  saveEnt.mutate()
                }}
                disabled={saveEnt.isPending}
              >
                Speichern
              </Button>
            </div>
          </div>
        )}

        {entitlements.length === 0 ? (
          <p className="text-xs text-text-tertiary">
            Keine Urlaubsansprüche hinterlegt. Es wird der Wert aus Allgemein als Fallback genutzt.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border-subtle text-text-tertiary">
                  <th className="text-left py-1 px-1 font-medium">Zeitraum</th>
                  <th className="text-right py-1 px-1 font-medium">Tage/Jahr</th>
                  <th className="text-left py-1 px-1 font-medium">Notiz</th>
                  <th className="py-1 px-1" />
                </tr>
              </thead>
              <tbody>
                {entitlements.map((e) => (
                  <tr key={e.id} className="border-b border-border-subtle hover:bg-bg-overlay/30">
                    <td className="py-1.5 px-1 text-text-primary">
                      {e.validFrom}
                      {e.validTo ? ` – ${e.validTo}` : ' – offen'}
                    </td>
                    <td className="py-1.5 px-1 text-right tabular-nums font-medium">
                      {Number(e.daysPerYear).toFixed(1).replace('.', ',')}
                    </td>
                    <td className="py-1.5 px-1 text-text-tertiary truncate max-w-[180px]">
                      {e.notes ?? '—'}
                    </td>
                    <td className="py-1.5 px-1">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => startEditEnt(e)}
                          aria-label="Bearbeiten"
                          className="text-text-tertiary hover:text-accent"
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={async () => {
                            const ok = await confirm({
                              title: 'Urlaubsanspruch löschen',
                              description: 'Dieser Eintrag wird unwiderruflich gelöscht.',
                              variant: 'danger',
                              confirmLabel: 'Löschen',
                            })
                            if (ok) deleteEnt.mutate(e.id)
                          }}
                          aria-label="Löschen"
                          className="text-text-tertiary hover:text-danger"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Vacation Adjustments ────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
            Urlaubsanpassungen
          </h3>
          <button
            type="button"
            onClick={() => (showVacAdjForm ? resetVacAdjForm() : setShowVacAdjForm(true))}
            className="text-xs text-accent hover:underline"
          >
            {showVacAdjForm ? 'Abbrechen' : '+ Anpassung hinzufügen'}
          </button>
        </div>

        {showVacAdjForm && (
          <div className="rounded-xl border border-border-subtle p-3 mb-3 space-y-3 bg-bg-overlay/30">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-2xs">Typ</Label>
                <Select
                  value={vacAdjType}
                  onChange={(e) =>
                    setVacAdjType(
                      e.target.value as 'carryover' | 'payout' | 'expiry' | 'correction',
                    )
                  }
                  className="h-8 text-xs"
                >
                  <option value="carryover">Übertrag</option>
                  <option value="payout">Auszahlung</option>
                  <option value="expiry">Verfall</option>
                  <option value="correction">Korrektur</option>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-2xs">Jahr</Label>
                <Input
                  type="number"
                  min="2000"
                  max="2100"
                  value={vacAdjYear}
                  onChange={(e) => setVacAdjYear(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-2xs">Tage (+/-)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={vacAdjDays}
                  onChange={(e) => setVacAdjDays(e.target.value)}
                  placeholder="z.B. 5 oder -2"
                  className="h-8 text-xs"
                />
              </div>
              {vacAdjType === 'carryover' && (
                <div className="space-y-1">
                  <Label className="text-2xs">Ablauf-Datum</Label>
                  <Input
                    type="date"
                    value={vacAdjExpires}
                    onChange={(e) => setVacAdjExpires(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-2xs">Beschreibung</Label>
              <Input
                value={vacAdjDesc}
                onChange={(e) => setVacAdjDesc(e.target.value)}
                placeholder="z.B. Resturlaub 2025"
                className="h-8 text-xs"
              />
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                type="button"
                onClick={() => {
                  if (!vacAdjYear || !vacAdjDays) {
                    notify.error('Jahr und Tage sind Pflicht')
                    return
                  }
                  saveVacAdj.mutate()
                }}
                disabled={saveVacAdj.isPending}
              >
                Speichern
              </Button>
            </div>
          </div>
        )}

        {vacAdjustments.length === 0 ? (
          <p className="text-xs text-text-tertiary">Keine Urlaubsanpassungen vorhanden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border-subtle text-text-tertiary">
                  <th className="text-left py-1 px-1 font-medium">Jahr</th>
                  <th className="text-left py-1 px-1 font-medium">Typ</th>
                  <th className="text-right py-1 px-1 font-medium">Tage</th>
                  <th className="text-left py-1 px-1 font-medium">Ablauf</th>
                  <th className="text-left py-1 px-1 font-medium">Beschreibung</th>
                  <th className="py-1 px-1" />
                </tr>
              </thead>
              <tbody>
                {vacAdjustments.map((v) => {
                  const days = Number(v.days)
                  return (
                    <tr key={v.id} className="border-b border-border-subtle hover:bg-bg-overlay/30">
                      <td className="py-1.5 px-1 text-text-primary tabular-nums">{v.year}</td>
                      <td className="py-1.5 px-1 text-text-secondary">
                        {VAC_ADJ_TYPE_LABELS[v.type] ?? v.type}
                      </td>
                      <td
                        className={cn(
                          'py-1.5 px-1 text-right tabular-nums font-medium',
                          days > 0
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : days < 0
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-text-secondary',
                        )}
                      >
                        {days > 0 ? '+' : ''}
                        {days.toFixed(1).replace('.', ',')}
                      </td>
                      <td className="py-1.5 px-1 text-text-tertiary tabular-nums">
                        {v.expiresAt ?? '—'}
                      </td>
                      <td className="py-1.5 px-1 text-text-tertiary truncate max-w-[140px]">
                        {v.description ?? '—'}
                      </td>
                      <td className="py-1.5 px-1">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => startEditVacAdj(v)}
                            aria-label="Bearbeiten"
                            className="text-text-tertiary hover:text-accent"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={async () => {
                              const ok = await confirm({
                                title: 'Urlaubsanpassung löschen',
                                description: 'Diese Anpassung wird unwiderruflich gelöscht.',
                                variant: 'danger',
                                confirmLabel: 'Löschen',
                              })
                              if (ok) deleteVacAdj.mutate(v.id)
                            }}
                            aria-label="Löschen"
                            className="text-text-tertiary hover:text-danger"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
